import React from 'react';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import { colors } from '../../utils/themeUtils';

// Common chart margins with responsive values
export const CHART_MARGINS = {
  top: 15,
  bottom: 75,
  left: 60,
  right: 40
};

// Smaller margins for mobile devices - increased left margin to prevent y-axis overflow
export const MOBILE_CHART_MARGINS = {
  top: 8,
  bottom: 55,
  left: 70,
  right: 20
};

// Modern card styling for all dashboard charts
export const MODERN_CARD_STYLE = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease-in-out',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  '&:hover': {
    boxShadow: '0 8px 25px 0 rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)'
  }
};

// Modern card header styling
export const CARD_HEADER_STYLE = {
  padding: { xs: '6px 10px 0px 10px', md: '8px 12px 0px 12px' },
  height: { xs: '48px', md: '56px' },
  display: 'flex',
  alignItems: 'center'
};

// Modern card content styling for chart containers
export const CARD_CONTENT_STYLE = {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: { xs: '2px 8px 6px 2px', md: '2px 12px 8px 4px' },
  overflow: 'hidden',
  position: 'relative'
};

// Common chart styling based on screen size
export const getChartStyling = (isSmallScreen) => ({
  cursor: 'pointer',
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  maxHeight: '100%', // Prevent infinite stretching
  '.MuiChartsAxis-label': {
    fill: '#546E7A',
    fontWeight: 500,
    fontSize: isSmallScreen ? 9 : 12
  },
  '.MuiChartsAxis-tickLabel': {
    fill: '#546E7A',
    fontSize: isSmallScreen ? 10 : 11
  },
  // Ensure y-axis ticks stay within boundaries
  '.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
    textAnchor: 'end',
    dominantBaseline: 'middle',
    transform: isSmallScreen ? 'translate(-2px, 0)' : 'none'
  }
});

// Common axis styling based on screen size
export const getXAxisConfig = (data, isSmallScreen) => ({
  data,
  scaleType: 'band',
  tickLabelStyle: {
    angle: 45,
    textAnchor: 'start',
    fontSize: isSmallScreen ? 10 : 12,
    dominantBaseline: 'middle',
    overflow: 'visible'
  },
  // Display a maximum of 13 labels regardless of dataset size
  tickLabelInterval: (_, i) => {
    // If we have 13 or fewer data points, show all labels
    if (data.length <= 13) return true;
    
    // Otherwise, calculate the interval needed to show approximately 13 labels
    const interval = Math.ceil(data.length / 13);
    
    // Show labels at calculated intervals
    return i % interval === 0;
  },
});

// Y-axis common configuration
export const getYAxisConfig = (currencySymbol, view, isSmallScreen, min = undefined, max = undefined) => ({
  valueFormatter: (value) => formatAxisValue(value, currencySymbol, view),
  tickLabelStyle: {
    fontSize: isSmallScreen ? 10 : 11,
    // Ensure proper alignment
    textAnchor: 'end'
  },
  tickSize: isSmallScreen ? 3 : 5,
  tickCount: isSmallScreen ? 3 : 5, // Reduce number of ticks on small screens
  min,
  max
});

// Get responsive chart margins based on screen size
export const getResponsiveMargins = (isSmallScreen) => {
  return isSmallScreen ? MOBILE_CHART_MARGINS : CHART_MARGINS;
};

// Common series styling for impact charts
export const getImpactSeriesConfig = (positiveData, negativeData, currencySymbol, view) => [
  {
    label: 'Positive',
    data: positiveData,
    stack: 'impact',
    stackOffset: 'diverging',
    color: colors.positiveGreen,
    valueFormatter: (value) => formatChartValue(value, currencySymbol, view),
  },
  {
    label: 'Negative',
    data: negativeData,
    stack: 'impact',
    color: colors.negativeRed,
    valueFormatter: (value) => formatChartValue(value, currencySymbol, view),
  },
];

// A hook to get responsive chart props
export const useResponsiveChartProps = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  return {
    isSmallScreen,
    chartSx: getChartStyling(isSmallScreen),
    margin: getResponsiveMargins(isSmallScreen),
    slotProps: {
      legend: { 
        hidden: true 
      }
    }
  };
};

// Standard Chart Component for consistent styling and settings
export const StandardChartCard = ({ 
  title, 
  children, 
  action = null, 
  showTitle = true,
  titleIcon = null
}) => {
  return (
    <Card sx={{
      ...MODERN_CARD_STYLE,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {showTitle && (
        <CardHeader
          sx={CARD_HEADER_STYLE}
          title={<DashboardCardTitle icon={titleIcon}>{title}</DashboardCardTitle>}
          action={action}
        />
      )}
      <CardContent sx={CARD_CONTENT_STYLE}>
        {children}
      </CardContent>
    </Card>
  );
};

// Dashboard title component with consistent styling
export const DashboardCardTitle = ({ children, icon }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Typography
      variant="h6"
      sx={{
        fontSize: {
          xs: '14px',
          sm: '16px',
          md: '18px'
        },
        fontWeight: 600,
        color: '#1a2027',
        mb: 0,
        pb: 0,
        whiteSpace: isSmallScreen ? 'nowrap' : 'normal',
        overflow: isSmallScreen ? 'hidden' : 'visible',
        textOverflow: isSmallScreen ? 'ellipsis' : 'clip',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        lineHeight: 1.3
      }}
    >
      {icon && React.cloneElement(icon, {
        sx: { 
          fontSize: isSmallScreen ? '0.9em' : '1em',
          color: theme.palette.primary.main,
          mt: '0.15em',
          ...icon.props.sx
        }
      })}
      {children}
    </Typography>
  );
};

// Internal implementation of formatCurrency to avoid circular dependencies
const internalFormatCurrency = (value, symbol, view) => {
  if (value == null) return 'N/A';
  if (view !== "Sales") return value.toFixed(4);
  const rounded = Math.round(value);
  const absValue = Math.abs(rounded);
  const formatted = absValue.toLocaleString();
  return value < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
};

// Helper for formatting chart values - updated to match original implementation
export const formatChartValue = (value, currencySymbol, view) => {
  if (value == null) return view === "Sales" ? 'N/A' : 'N/A ★';

  if (view === "Sales") {
    const formatted = internalFormatCurrency(value, currencySymbol, view);
    return value > 0 ? `+${formatted}` : formatted;
  }

  // For Rating view - match the Comments Panel and Reviews Table format
  const formattedValue = value.toFixed(4); // Fixed precision for tooltips
  const prefixedValue = value > 0 ? `+${formattedValue}` : formattedValue;
  return `${prefixedValue} ★`;
};

// Helper for formatting axis values - updated to match original implementation
export const formatAxisValue = (value, currencySymbol, view) => {
  if (value == null) return view === "Sales" ? 'N/A' : 'N/A ★';

  // For xs screens, use shorter display format
  const isCompactDisplay = typeof window !== 'undefined' && window.innerWidth < 600;

  if (view === "Sales") {
    if (isCompactDisplay) {
      // For small screens, use more compact format
      const absValue = Math.abs(value);
      if (absValue >= 1000) {
        const formatted = (absValue / 1000).toFixed(1) + 'k';
        return value < 0 ? `-${currencySymbol}${formatted}` : `${currencySymbol}${formatted}`;
      }
    }

    const formatted = internalFormatCurrency(value, currencySymbol, view);
    return value > 0 ? `+${formatted}` : formatted;
  }

  // For Rating view - dynamic precision
  // Use more compact format for small screens
  if (isCompactDisplay) {
    const absValue = Math.abs(value);
    // Just use the most significant digits on small screens
    const formattedValue = absValue.toFixed(1);
    const prefixedValue = value > 0 ? `+${formattedValue}` : `-${formattedValue}`;
    return `${prefixedValue}★`;
  }

  const formattedValue = value.toString(); // Preserve original precision
  const prefixedValue = value > 0 ? `+${formattedValue}` : formattedValue;
  return `${prefixedValue} ★`;
};

// Custom tooltip component for charts that mimics the Comments Panel and Reviews Table style
export const CustomChartTooltip = ({ active, payload, label, currencySymbol, view }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div style={{
      backgroundColor: 'rgba(10, 37, 64, 0.9)',
      padding: '10px 12px',
      fontSize: '0.85rem',
      borderRadius: '8px',
      boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
      border: 'none',
      color: '#E5E8EC',
      maxWidth: 'none',
      whiteSpace: 'nowrap'
    }}>
      <div style={{ marginBottom: '5px', fontSize: '0.85rem' }}>{label}</div>
      {payload.map((entry, index) => {
        const isPositive = entry.value > 0;
        const valueColor = isPositive ? colors.positiveGreen : entry.value < 0 ? colors.negativeRed : '#E5E8EC';
        
        // Format the value similar to the Comments Panel and Reviews Table
        let formattedValue;
        if (view === "Sales") {
          const numericValue = entry.value;
          const absValue = Math.abs(Math.round(numericValue));
          const formatted = absValue.toLocaleString();
          formattedValue = isPositive 
            ? `+${currencySymbol}${formatted}` 
            : `-${currencySymbol}${formatted}`;
        } else {
          // For Rating view
          const value = entry.value.toFixed(4);
          formattedValue = isPositive ? `+${value} ★` : `${value} ★`;
        }
        
        return (
          <div key={`item-${index}`} style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginTop: '3px'
          }}>
            <span style={{ marginRight: '10px', fontSize: '0.85rem' }}>{entry.name}:</span>
            <span style={{ 
              color: valueColor,
              fontWeight: 600,
              fontSize: '0.85rem'
            }}>
              {formattedValue}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default {
  CHART_MARGINS,
  MOBILE_CHART_MARGINS,
  DashboardCardTitle,
  MODERN_CARD_STYLE,
  CARD_HEADER_STYLE,
  CARD_CONTENT_STYLE,
  getChartStyling,
  getXAxisConfig,
  getYAxisConfig,
  getResponsiveMargins,
  getImpactSeriesConfig,
  useResponsiveChartProps,
  formatChartValue,
  formatAxisValue,
  StandardChartCard,
  CustomChartTooltip
};
